import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  ChevronDown,
  Loader2,
  GitCompare,
  FolderOpen,
  File,
  ArrowRight,
  Zap,
  Eye,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Info,
  Search,
  Check,
} from "lucide-react";
import documentCompareService from "../../services/document-compare.service";
import uploadedFilesService, {
  UploadedFile,
} from "../../services/uploaded-files.service";
import documentStoresService from "../../services/document-stores.service";
import Loader from "../../widgets/Loader";
import CompareDetailsModal from "../../components/documents/CompareDetailsModal";
import Can from "../../components/auth/Can";

interface DocumentStoreOption {
  _id: string;
  folder_name: string;
  folder_description?: string;
  document_store_type?: string;
  id: string;
}

const DocumentCompare: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [documentStoreId, setDocumentStoreId] = useState<string>("");
  const [documentStores, setDocumentStores] = useState<DocumentStoreOption[]>(
    [],
  );
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [sourceFileId, setSourceFileId] = useState<string>("");
  const [targetFileId, setTargetFileId] = useState<string>("");
  const [accuracyLevel, setAccuracyLevel] = useState<string>("MEDIUM");
  const [isComparing, setIsComparing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Comparison history
  const [compareHistory, setCompareHistory] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const pollingIntervalRef = useRef<any | null>(null);

  // Modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCompareData, setSelectedCompareData] = useState<any>(null);
  const [viewId, setViewId] = useState<string>("");

  // --- NEW: Custom Dropdown State for Folders ---
  const [isFolderOpen, setIsFolderOpen] = useState(false);
  const [folderSearchTerm, setFolderSearchTerm] = useState("");
  const folderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchDocumentStores();

    // Click outside listener for folder dropdown
    const handleClickOutside = (event: MouseEvent) => {
      if (
        folderRef.current &&
        !folderRef.current.contains(event.target as Node)
      ) {
        setIsFolderOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (documentStoreId) {
      fetchUploadedFiles();
      fetchCompareHistory();
    }
  }, [documentStoreId, currentPage]);

  useEffect(() => {
    const saved = localStorage.getItem("compareSelections") || "";
    if (saved) {
      const { documentStoreId, sourceFileId, targetFileId, accuracyLevel } =
        JSON.parse(saved);

      if (documentStoreId) setDocumentStoreId(documentStoreId);
      if (sourceFileId) setSourceFileId(sourceFileId);
      if (targetFileId) setTargetFileId(targetFileId);
      if (accuracyLevel) setAccuracyLevel(accuracyLevel);
    }
  }, []);

  // ✅ 2. Save to localStorage when any selection changes
  useEffect(() => {
    if (location.pathname.includes("/document-compare")) {
      localStorage.setItem(
        "compareSelections",
        JSON.stringify({
          documentStoreId,
          sourceFileId,
          targetFileId,
          accuracyLevel,
        }),
      );
    }
  }, [
    location.pathname,
    documentStoreId,
    sourceFileId,
    targetFileId,
    accuracyLevel,
  ]);

  // Remove localStorage when leaving (unless going to /view)
  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem("compareSelections");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      if (!location.pathname.includes(`/document-compare/result/${viewId}`)) {
        localStorage.removeItem("compareSelections");
      }
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [location.pathname, viewId]);

  const fetchDocumentStores = async () => {
    try {
      const response = await documentStoresService.getDocumentStoresDropdown();
      if (response.success) {
        setDocumentStores(response.data);
      }
    } catch (error: any) {
      console.error("Error fetching document stores:", error);
      toast.error("Failed to fetch document stores");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUploadedFiles = async () => {
    if (!documentStoreId) return;

    try {
      const response = await uploadedFilesService.getUploadedFilesDropdown({
        document_store_id: documentStoreId,
      });
      if (response.success) {
        setUploadedFiles(response.data.uploadedFilesList);
      }
    } catch (error: any) {
      console.error("Error fetching uploaded files:", error);
      toast.error("Failed to fetch uploaded files");
    }
  };

  const fetchCompareHistory = async (silent: boolean = false) => {
    if (!documentStoreId) return;

    if (!silent) {
      setIsLoadingHistory(true);
    }
    try {
      const response = await documentCompareService.getDocumentCompareList({
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        document_store_id: documentStoreId,
      });

      if (response.success) {
        setCompareHistory(response.data.documentCompareDatasList);
        setTotalCount(response.data.total_count);

        const needsPolling = response.data.documentCompareDatasList.some(
          (item) =>
            item.document_compare_status &&
            item.document_compare_status !== "DONE" &&
            item.document_compare_status !== "FAILED",
        );

        if (needsPolling) {
          startStatusPolling();
        }
      } else {
        if (!silent) {
          toast.error("Failed to fetch comparison history");
        }
      }
    } catch (error: any) {
      console.error("Error fetching compare history:", error);
      if (!silent) {
        toast.error("Failed to fetch comparison history");
      }
    } finally {
      if (!silent) {
        setIsLoadingHistory(false);
      }
    }
  };

  const startStatusPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    const pollStatus = async () => {
      if (!documentStoreId) return;

      setCompareHistory((currentData) => {
        const processingComparisons = currentData.filter(
          (item) =>
            item.document_compare_status &&
            item.document_compare_status !== "DONE" &&
            item.document_compare_status !== "FAILED",
        );

        if (processingComparisons.length === 0) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          return currentData;
        }

        const compareIds = processingComparisons.map((item) => item._id);

        documentCompareService
          .getStatusByIds({
            document_compare_ids: compareIds,
            document_store_id: documentStoreId,
          })
          .then((response) => {
            if (response?.data && Array.isArray(response.data)) {
              const completedItems = response.data.filter(
                (update) =>
                  update.document_compare_status === "DONE" ||
                  update.document_compare_status === "FAILED",
              );

              setCompareHistory((prevData: any) => {
                const updatedData = prevData.map((item: any) => {
                  const statusUpdate: any = response.data.find(
                    (update) => update._id === item._id,
                  );
                  if (statusUpdate) {
                    return {
                      ...item,
                      document_compare_status:
                        statusUpdate.document_compare_status,
                    };
                  }
                  return item;
                });

                const stillProcessing = updatedData.some(
                  (item: any) =>
                    item.document_compare_status &&
                    item.document_compare_status !== "DONE" &&
                    item.document_compare_status !== "FAILED",
                );

                if (!stillProcessing && pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                  if (completedItems.length > 0) {
                    fetchCompareHistory(true);
                  }
                }

                return updatedData;
              });
            }
          })
          .catch((error) => {
            console.error("Error polling compare statuses:", error);
          });

        return currentData;
      });
    };

    pollingIntervalRef.current = setInterval(pollStatus, 3000);
  };

  const handleCompare = async () => {
    if (!documentStoreId) {
      toast.error("Please select a folder");
      return;
    }

    if (!sourceFileId) {
      toast.error("Please select a source file");
      return;
    }

    if (!targetFileId) {
      toast.error("Please select a target file");
      return;
    }

    if (sourceFileId === targetFileId) {
      toast.error("Source and target files must be different");
      return;
    }

    if (!accuracyLevel) {
      toast.error("Please select an accuracy level");
      return;
    }

    setIsComparing(true);
    try {
      const response = await documentCompareService.compareDocuments(
        documentStoreId,
        {
          source_file_id: sourceFileId,
          target_file_id: targetFileId,
          ai_performance_level: accuracyLevel,
        },
      );

      if (response.success) {
        toast.success("Documents comparison initiated successfully!");
        // Refresh the history and start polling
        setTimeout(() => {
          fetchCompareHistory();
          startStatusPolling();
        }, 1000);
      } else {
        toast.error("Failed to compare documents");
      }
    } catch (error: any) {
      console.error("Error comparing documents:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to compare documents");
      }
    } finally {
      setIsComparing(false);
    }
  };

  const handleViewResult = (compareId: string) => {
    setViewId(compareId);
    navigate(`/document-compare/result/${compareId}`);
  };

  const handleViewDetails = (compareData: any) => {
    setSelectedCompareData(compareData);
    setShowDetailsModal(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFolderSelect = (folderId: string) => {
    setDocumentStoreId(folderId);
    setSourceFileId("");
    setTargetFileId("");
    setCurrentPage(1);
    setIsFolderOpen(false);
    setFolderSearchTerm("");
  };

  const getStatusBadge = (status: any) => {
    switch (status) {
      case "DONE":
        return {
          color: "bg-emerald-50 text-emerald-700 border border-emerald-200",
          text: "Completed",
          icon: CheckCircle2,
        };
      case "FAILED":
        return {
          color: "bg-rose-50 text-rose-700 border border-rose-200",
          text: "Failed",
          icon: XCircle,
        };
      case "IN_PROGRESS":
      case "PROCESSING":
        return {
          color: "bg-blue-50 text-blue-700 border border-blue-200",
          text: "Processing",
          icon: Loader2,
        };
      case "PENDING":
        return {
          color: "bg-amber-50 text-amber-700 border border-amber-200",
          text: "Pending",
          icon: AlertCircle,
        };
      default:
        return {
          color: "bg-slate-50 text-slate-700 border border-slate-200",
          text: status || "Unknown",
          icon: AlertCircle,
        };
    }
  };

  // --- Filter Logic ---
  const filteredFolders = documentStores.filter((store) =>
    store.folder_name.toLowerCase().includes(folderSearchTerm.toLowerCase()),
  );

  const selectedFolderObj = documentStores.find(
    (store) => store.id === documentStoreId,
  );

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40">
      {/* Modern Header with Glass Morphism */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200/60 shadow-sm">
        <div className="px-6 py-3">
          {/* Page Title & Description */}
          <div className="mb-3">
            <div className="flex items-center gap-3">
              {/* Icon */}
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-600">
                <GitCompare className="h-4 w-4 text-white" />
              </div>

              {/* Title + Description */}
              <div>
                <h1 className="text-lg font-bold text-slate-900">
                  Document Compare
                </h1>
                <p className="text-xs text-slate-600">
                  Compare two documents and highlight differences using
                  AI-powered analysis
                </p>
              </div>
            </div>
          </div>

          {/* Control Panel */}
          <div className="flex flex-col gap-3">
            {/* Document Store Selection */}
            <div className="grid grid-cols-12 gap-3 items-end">
              {/* --- CUSTOM FOLDER DROPDOWN WITH SEARCH --- */}
              <div className="col-span-6" ref={folderRef}>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1">
                  My Folders
                  <span className="text-rose-600">*</span>
                </label>
                <div className="relative group">
                  <button
                    type="button"
                    onClick={() => setIsFolderOpen(!isFolderOpen)}
                    className={`relative w-full text-left px-3 py-2.5 pl-10 bg-white border-2 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all duration-300 font-semibold shadow-sm hover:shadow-md flex items-center justify-between rounded-md
                      ${isFolderOpen ? "border-blue-500 ring-1 ring-blue-500" : "border-slate-200 hover:border-blue-300"}
                    `}
                  >
                    <span className="truncate block pr-6">
                      {selectedFolderObj ? (
                        <>
                          {selectedFolderObj.folder_name}
                          {selectedFolderObj.document_store_type && (
                            <span className="text-slate-500 font-normal ml-1">
                              • {selectedFolderObj.document_store_type}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-slate-500 text-opacity-80">
                          Select a folder...
                        </span>
                      )}
                    </span>

                    {/* Icons */}
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <FolderOpen className="h-4 w-4 text-emerald-500" />
                    </div>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <ChevronDown
                        className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${
                          isFolderOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </button>

                  {/* Dropdown Panel */}
                  {isFolderOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-xl animate-in fade-in zoom-in-95 duration-100">
                      {/* Search Input */}
                      <div className="p-2 border-b border-slate-100 sticky top-0 bg-white rounded-t-md">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                          <input
                            type="text"
                            className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded bg-slate-50 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 placeholder:text-slate-400"
                            placeholder="Search folders..."
                            value={folderSearchTerm}
                            onChange={(e) =>
                              setFolderSearchTerm(e.target.value)
                            }
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>

                      {/* Options List */}
                      <div className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                        {filteredFolders.length === 0 ? (
                          <div className="px-3 py-4 text-center text-xs text-slate-500">
                            No folders found
                          </div>
                        ) : (
                          <ul className="py-1">
                            {filteredFolders.map((store) => {
                              const isSelected = documentStoreId === store.id;
                              return (
                                <li
                                  key={store.id}
                                  onClick={() => handleFolderSelect(store.id)}
                                  className={`px-3 py-2 cursor-pointer flex items-center justify-between text-sm transition-colors
                                    ${
                                      isSelected
                                        ? "bg-blue-50 text-blue-700 font-medium"
                                        : "text-slate-700 hover:bg-slate-50 hover:text-blue-600"
                                    }
                                  `}
                                >
                                  <span className="truncate">
                                    {store.folder_name}
                                    {store.document_store_type && (
                                      <span
                                        className={`text-xs ml-1 ${
                                          isSelected
                                            ? "text-blue-500"
                                            : "text-slate-400"
                                        }`}
                                      >
                                        • {store.document_store_type}
                                      </span>
                                    )}
                                  </span>
                                  {isSelected && (
                                    <Check className="h-3.5 w-3.5 text-blue-600" />
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* --- END CUSTOM DROPDOWN --- */}

              {/* Accuracy Level - Kept as Native Select as requested, or standard style */}
              <div className="col-span-6">
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Accuracy Level
                </label>
                <div className="relative group">
                  <select
                    value={accuracyLevel}
                    onChange={(e) => setAccuracyLevel(e.target.value)}
                    disabled={!documentStoreId}
                    className="w-full appearance-none px-3 py-2.5 bg-white border border-slate-200 rounded-md text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all duration-200 cursor-pointer font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:border-violet-300"
                  >
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* File Selection Row */}
            <div className="grid grid-cols-12 gap-3 items-end">
              {/* Source File */}
              <div className="col-span-5">
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1">
                  Source File
                  <span className="text-rose-600">*</span>
                </label>
                <div className="relative group">
                  <select
                    value={sourceFileId}
                    onChange={(e) => setSourceFileId(e.target.value)}
                    disabled={!documentStoreId}
                    className="w-full appearance-none px-3 py-2.5 pl-10 bg-white border border-slate-200 rounded-md text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 cursor-pointer font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:border-blue-300"
                  >
                    <option value="">Select source file...</option>
                    {uploadedFiles.map((file) => (
                      <option key={file._id} value={file._id}>
                        {file.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <File className="h-4 w-4 text-blue-500" />
                  </div>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </div>
                </div>
              </div>

              {/* Arrow Icon */}
              <div className="col-span-2 flex items-center justify-center pb-2">
                <ArrowRight className="h-6 w-6 text-indigo-500" />
              </div>

              {/* Target File */}
              <div className="col-span-5">
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1">
                  Target File
                  <span className="text-rose-600">*</span>
                </label>
                <div className="relative group">
                  <select
                    value={targetFileId}
                    onChange={(e) => setTargetFileId(e.target.value)}
                    disabled={!documentStoreId}
                    className="w-full appearance-none px-3 py-2.5 pl-10 bg-white border border-slate-200 rounded-md text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-green-500/50 transition-all duration-200 cursor-pointer font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:border-green-300"
                  >
                    <option value="">Select target file...</option>
                    {uploadedFiles.map((file) => (
                      <option key={file._id} value={file._id}>
                        {file.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <File className="h-4 w-4 text-green-500" />
                  </div>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </div>
                </div>
              </div>
            </div>
            {/* File Info Section */}
            <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm">
              <div className="flex items-center mb-2">
                <div className="flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full mr-2">
                  <Info className="w-4 h-4 text-blue-700" />
                </div>
                <span className="font-semibold text-blue-800">
                  Document compare Info
                </span>
              </div>
              <p className="text-gray-700 leading-snug ml-8">
                Only <span className="font-semibold">PDF files</span> are
                supported for document comparison. Please make sure both source
                and target files are in{" "}
                <span className="font-semibold">PDF</span> format. Other file
                types will not be processed.
              </p>
            </div>

            {/* Compare Button Row */}
            <div className="flex justify-end">
              <Can permission="documents:compare">
                <button
                  onClick={handleCompare}
                  disabled={
                    isComparing ||
                    !documentStoreId ||
                    !sourceFileId ||
                    !targetFileId
                  }
                  className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 disabled:from-slate-300 disabled:to-slate-400 text-white font-bold rounded-md transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 text-sm flex items-center justify-center gap-1.5"
                  title="Compare documents"
                >
                  {isComparing ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Comparing</span>
                    </>
                  ) : (
                    <>
                      <Zap className="w-3.5 h-3.5" />
                      <span>Compare</span>
                    </>
                  )}
                </button>
              </Can>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="py-4">
        {isLoading ? (
          <div className="min-h-[60vh] flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <Loader />
            </div>
          </div>
        ) : !documentStoreId ? (
          <div className="min-h-[60vh] flex items-center justify-center">
            <div className="text-center max-w-md">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-indigo-100 to-violet-100 mb-6">
                <GitCompare className="h-12 w-12 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Select Folder to Begin
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Choose a Folder from the dropdown above to start comparing
                documents with AI. Select two files from your document store to
                see their differences.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Section Header */}
            <div className="mb-3">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                Comparison History
                <span className="px-2 py-0.5 text-xs font-bold bg-indigo-100 text-indigo-700 rounded-full">
                  {totalCount}
                </span>
              </h2>
            </div>

            {/* Comparison History Table */}
            <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
              {/* Table Header */}
              <div className="bg-gradient-to-r from-slate-50 to-indigo-50/30 px-6 py-3 border-b border-slate-200">
                <div className="grid grid-cols-12 gap-6 text-xs font-bold text-slate-600 uppercase tracking-wide">
                  <div className="col-span-1">S.No</div>
                  <div className="col-span-3">Source File</div>
                  <div className="col-span-3">Target File</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-3">Actions</div>
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-slate-100">
                {isLoadingHistory ? (
                  <div className="px-6 py-12 text-center">
                    <Loader />
                  </div>
                ) : compareHistory && compareHistory.length > 0 ? (
                  compareHistory.map((item, index) => {
                    const statusBadge = getStatusBadge(
                      item.document_compare_status,
                    );
                    const StatusIcon = statusBadge.icon;
                    const isLoading =
                      item.document_compare_status !== "DONE" &&
                      item.document_compare_status !== "FAILED";
                    const serialNumber =
                      (currentPage - 1) * pageSize + index + 1;

                    return (
                      <div
                        key={item._id}
                        className="grid grid-cols-12 gap-6 px-6 py-3.5 hover:bg-slate-50/80 transition-all duration-200 group"
                      >
                        {/* Serial Number */}
                        <div className="col-span-1 flex items-center">
                          <span className="text-sm font-bold text-slate-600">
                            {serialNumber}
                          </span>
                        </div>

                        {/* Source File */}
                        <div className="col-span-3 flex items-center">
                          <div className="flex items-center gap-2 min-w-0">
                            <File className="h-4 w-4 text-blue-500 flex-shrink-0" />
                            <span className="text-sm font-semibold text-slate-900 truncate">
                              {item.source_file_data?.name || "Unknown"}
                            </span>
                          </div>
                        </div>

                        {/* Target File */}
                        <div className="col-span-3 flex items-center">
                          <div className="flex items-center gap-2 min-w-0">
                            <File className="h-4 w-4 text-green-500 flex-shrink-0" />
                            <span className="text-sm font-semibold text-slate-900 truncate">
                              {item.target_file_data?.name || "Unknown"}
                            </span>
                          </div>
                        </div>

                        {/* Status */}
                        <div className="col-span-2 flex items-center">
                          <span
                            className={`px-3 py-1.5 text-xs font-bold rounded-lg flex items-center gap-1.5 ${statusBadge.color}`}
                          >
                            {isLoading ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 text-blue-600 animate-spin" />
                                {statusBadge.text}
                              </>
                            ) : (
                              <>
                                <StatusIcon className="h-3.5 w-3.5" />
                                {statusBadge.text}
                              </>
                            )}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="col-span-3 flex items-center gap-2">
                          <button
                            onClick={() => handleViewDetails(item)}
                            className="group/btn p-2.5 rounded-xl border-2 border-slate-200 bg-white hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-md"
                            title="View Details"
                          >
                            <Info className="h-4 w-4 text-slate-600 group-hover/btn:text-blue-600 transition-colors" />
                          </button>
                          <button
                            onClick={() => handleViewResult(item._id)}
                            disabled={item.document_compare_status !== "DONE"}
                            className="group/btn p-2.5 rounded-xl border-2 border-slate-200 bg-white hover:bg-indigo-50 hover:border-indigo-300 transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:bg-white disabled:hover:border-slate-200"
                            title="View Result"
                          >
                            <Eye className="h-4 w-4 text-slate-600 group-hover/btn:text-indigo-600 transition-colors" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="px-6 py-12 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-3">
                      <GitCompare className="h-8 w-8 text-slate-300" />
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-1">
                      No Comparisons Yet
                    </h3>
                    <p className="text-sm text-slate-500 max-w-md mx-auto">
                      Select files and click compare to start analyzing
                      differences
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Modern Pagination */}
            {totalPages > 1 && totalCount > 0 && (
              <div className="mt-4 bg-white rounded-lg border border-slate-200 px-6 py-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-600">
                    {totalCount > 0 && (
                      <span>
                        Showing{" "}
                        <span className="text-indigo-600 font-bold">
                          {startItem}–{endItem}
                        </span>{" "}
                        of{" "}
                        <span className="text-slate-900 font-bold">
                          {totalCount}
                        </span>{" "}
                        comparisons
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2.5 rounded-xl border-2 border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 active:scale-95 disabled:hover:scale-100"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>

                    <div className="flex items-center gap-1.5">
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`min-w-[40px] h-10 px-4 rounded-xl text-sm font-bold transition-all duration-200 ${
                                currentPage === pageNum
                                  ? "bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-200 scale-105"
                                  : "bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 hover:scale-105 active:scale-95"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        },
                      )}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2.5 rounded-xl border-2 border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 active:scale-95 disabled:hover:scale-100"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Details Modal */}
      <CompareDetailsModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        data={selectedCompareData}
      />
    </div>
  );
};

export default DocumentCompare;
